import React from "react";
import { api } from "./utils/api";
import { addFloatingPanel } from "./utils/DockerviewSettings";
import { ArticleViewer } from "./ArticleViewer";

interface InterceptedItem {
  url: string;
  source: string | null;
  content: string;
  table: "articles" | "tweets";
  published_at: number; // unix seconds
  read: number; // 0, 1, 2 (per-user mark, same semantics as News Search)
}

interface ExtendedWatchlistItem {
  investing_id: number;
  ticker: string;
  company_name: string;
  sector: string;
  regular_hours_last: number;
  regular_hours_change_abs: number;
  regular_hours_change_rel: number;
  premarket_active: boolean;
  premarket_last: number;
  premarket_change_abs: number;
  premarket_change_rel: number;
  marked: string | null;
  next_earnings: string | null;
  intercepted: InterceptedItem[];
}

interface ExtendedWatchlistPage {
  sector: string;
  items: ExtendedWatchlistItem[];
  total: number;
}

type GroupBy = "all" | "by_sector" | "marked_only" | "in_watchlist";
type SortBy = "regular_hours_change_rel" | "premarket_change_rel" | "abs_pre_minus_reg";
type Order = "asc" | "desc";
type MarkFilter = "0" | "1" | "2" | "any";

interface FetchParams {
  group_by: GroupBy;
  sort_by: SortBy;
  order: Order;
  days: number;
  mark: MarkFilter;
  only_with_intercepts: boolean;
  query: string;
}

const PAGE_SIZE = 100;

function nycTimestamp(): string {
  const now = new Date();
  const nyc = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${nyc.getFullYear()}-${pad(nyc.getMonth() + 1)}-${pad(nyc.getDate())} ${pad(nyc.getHours())}:${pad(nyc.getMinutes())}:${pad(nyc.getSeconds())}`;
}

function formatPublished(unixSeconds: number): string {
  const date = new Date(unixSeconds * 1000);
  const nyc = new Date(date.toLocaleString("en-US", { timeZone: "America/New_York" }));
  const pad = (n: number) => String(n).padStart(2, "0");
  const dayOfWeek = nyc.toLocaleDateString("en-US", { weekday: "short" });
  return `${nyc.getFullYear()}-${pad(nyc.getMonth() + 1)}-${pad(nyc.getDate())} ${pad(nyc.getHours())}:${pad(nyc.getMinutes())} (${dayOfWeek})`;
}

function addMarkClass(mark: number): string {
  switch (mark) {
    case 0:
      return "bg-gray-400";
    case 1:
      return "bg-blue-600";
    case 2:
      return "bg-yellow-600";
    default:
      return "bg-gray-400";
  }
}

const ExtendedWatchlistIntercepted = ({
  investingId,
  companyName,
  items,
  setActiveArticleUrl,
  markItem,
}: {
  investingId: number;
  companyName: string;
  items: InterceptedItem[];
  setActiveArticleUrl: (url: string) => void;
  markItem: (it: InterceptedItem, read: number) => void;
}) => {
  const [sourceFilter, setSourceFilter] = React.useState<string>("All");

  const sources = Array.from(new Set(items.map((i) => i.source || "unknown"))).sort();
  const filtered = (sourceFilter === "All" ? items : items.filter((i) => (i.source || "unknown") === sourceFilter))
    .slice()
    .sort((a, b) => b.published_at - a.published_at);

  return (
    <div className="col-span-4 sm:col-span-6 px-1 pb-1 w-0 min-w-full">
      <p className="font-semibold break-words">{companyName}</p>
      <p className="font-semibold break-words">Intercepted:</p>
      {items.length > 0 && (
        <div className="flex flex-row flex-wrap items-center gap-3 mb-1">
          {["All", ...sources].map((source) => (
            <label key={source} className="flex items-center gap-1">
              <input
                type="radio"
                name={`ext-intercept-source-${investingId}`}
                value={source}
                checked={sourceFilter === source}
                onChange={() => setSourceFilter(source)}
              />
              <span>{source}</span>
            </label>
          ))}
        </div>
      )}
      {filtered.length === 0 && <p className="text-gray-400">No intercepted items</p>}
      {filtered.map((it) => (
        <div key={it.url} className="flex items-start gap-2 mb-1">
          <div
            className={`min-w-5 h-5 mt-0.5 cursor-pointer shrink-0 ${addMarkClass(it.read)}`}
            title="Click to cycle mark (unmarked / read / saved)"
            onClick={() => markItem(it, it.read + 1)}
          ></div>
          <div className="break-words whitespace-pre-wrap">
            [{formatPublished(it.published_at)}] [{it.table} - {it.source}] {it.content}
            <div className="flex gap-2 mt-1 text-sm items-center">
              <a href={it.url} target="_blank" rel="noreferrer">
                (source)
              </a>
              {it.table === "articles" && (
                <a
                  href="/"
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveArticleUrl(it.url);
                  }}
                >
                  (text)
                </a>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export const ExtendedWatchlist = (props?: any) => {
  const [sector, setSector] = React.useState<string>("");
  const [items, setItems] = React.useState<ExtendedWatchlistItem[]>([]);
  const [total, setTotal] = React.useState<number>(0);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>("");

  const [groupBy, setGroupBy] = React.useState<GroupBy>("all");
  const [sortBy, setSortBy] = React.useState<SortBy>("premarket_change_rel");
  const [order, setOrder] = React.useState<Order>("desc");

  const [daysInput, setDaysInput] = React.useState<string>("2"); // 48h, as the general Watchlist keeps its intercepts
  const [markFilter, setMarkFilter] = React.useState<MarkFilter>("0");
  const [onlyWithIntercepts, setOnlyWithIntercepts] = React.useState<boolean>(true);

  const [tickerFilter, setTickerFilter] = React.useState<string>("");
  // present for parity with Watchlist, but intentionally not wired up yet - no sector data exists
  const [sectorFilter, setSectorFilter] = React.useState<string>("");

  const [expandedIntercepted, setExpandedIntercepted] = React.useState<Set<number>>(new Set());

  const [activeArticleUrl, setActiveArticleUrl] = React.useState<string>("");
  const containerRef = React.useRef<HTMLDivElement>(null);
  const sentinelRef = React.useRef<HTMLDivElement>(null);
  const savedScrollTop = React.useRef<number>(0);

  // params of the last Fetch - further pages must use these, not whatever the form shows now
  const paramsRef = React.useRef<FetchParams | null>(null);
  // bumped on every new Fetch, so a page still in flight from an older one is dropped
  const requestIdRef = React.useRef(0);
  const loadingRef = React.useRef(false);

  function loadPage(params: FetchParams, offset: number) {
    const requestId = requestIdRef.current;
    loadingRef.current = true;
    setIsLoading(true);

    api
      .get("/watchlist-extended", { params: { ...params, offset, limit: PAGE_SIZE } })
      .then((response) => {
        if (requestId !== requestIdRef.current) return;
        const data: ExtendedWatchlistPage = response.data;
        setSector(data.sector);
        setTotal(data.total);
        setItems((prev) => {
          const base = offset === 0 ? [] : prev;
          // prices update every 30s, so the order can shift between pages - skip repeats
          const seen = new Set(base.map((i) => i.investing_id));
          return [...base, ...data.items.filter((i) => !seen.has(i.investing_id))];
        });
      })
      .catch((err) => {
        if (requestId !== requestIdRef.current) return;
        console.error("Error fetching extended watchlist:", err);
        setError(err.response?.data?.message || "Error fetching extended watchlist.");
      })
      .finally(() => {
        if (requestId !== requestIdRef.current) return;
        loadingRef.current = false;
        setIsLoading(false);
      });
  }

  function fetchWatchlistExtended(query: string = tickerFilter) {
    setError("");

    const parsedDays = Math.max(1, parseInt(daysInput, 10) || 1);
    setDaysInput(String(parsedDays));

    const params: FetchParams = {
      group_by: groupBy,
      sort_by: sortBy,
      order: order,
      days: parsedDays,
      mark: markFilter,
      only_with_intercepts: onlyWithIntercepts,
      query: query.trim(),
    };
    paramsRef.current = params;
    requestIdRef.current++;
    setItems([]);
    setTotal(0);
    if (containerRef.current) containerRef.current.scrollTop = 0;
    loadPage(params, 0);
  }

  // initial load; further form changes apply only when the user presses Fetch
  React.useEffect(() => {
    fetchWatchlistExtended();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // the ticker search runs server-side (the list is paged), debounced while typing
  const isFirstRender = React.useRef(true);
  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const tf = tickerFilter.trim();
    if (tf.length === 1) return; // like Watchlist, searching starts at 2 characters
    const t = setTimeout(() => fetchWatchlistExtended(tf), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickerFilter]);

  // load the next page once the end of the list scrolls into view
  React.useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting || loadingRef.current || !paramsRef.current) return;
        if (items.length >= total) return;
        loadPage(paramsRef.current, items.length);
      },
      { root: containerRef.current, rootMargin: "400px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, total, activeArticleUrl]);

  function toggleIntercepted(investingId: number) {
    setExpandedIntercepted((prev) => {
      const next = new Set(prev);
      if (next.has(investingId)) next.delete(investingId);
      else next.add(investingId);
      return next;
    });
  }

  function markItem(it: InterceptedItem, read: number) {
    const normalizedRead = read % 3;
    // capture the pre-update read value via the functional updater so a revert-on-error
    // targets the right value even if other marks landed in between
    let prevRead = 0;

    // functional updater: always applies on top of the latest state, never a stale snapshot
    // from the render that created this closure, so rapid successive marks don't clobber each other
    setItems((prevItems) =>
      prevItems.map((item) => ({
        ...item,
        intercepted: item.intercepted.map((a) => {
          if (a.url !== it.url) return a;
          prevRead = a.read;
          return { ...a, read: normalizedRead };
        }),
      })),
    );

    const revert = () => {
      setItems((prevItems) =>
        prevItems.map((item) => ({
          ...item,
          intercepted: item.intercepted.map((a) => (a.url === it.url ? { ...a, read: prevRead } : a)),
        })),
      );
    };

    api
      .post("/mark", { url: it.url, read: normalizedRead, table: it.table })
      .then((response) => {
        if (!response.data?.success) revert();
      })
      .catch((err) => {
        console.error("Error marking intercepted item:", err);
        revert();
      });
  }

  function handleMark(item: ExtendedWatchlistItem) {
    const markedAt = nycTimestamp();
    setItems((prevItems) =>
      prevItems.map((i) => (i.investing_id === item.investing_id ? { ...i, marked: markedAt } : i)),
    );
    api.post(`/watchlist-extended/${item.investing_id}/mark`).catch((err) => {
      setError(err.response?.data?.message || "Error marking item.");
      setItems((prevItems) =>
        prevItems.map((i) => (i.investing_id === item.investing_id ? { ...i, marked: null } : i)),
      );
    });
  }

  function handleUnmark(item: ExtendedWatchlistItem) {
    setItems((prevItems) =>
      prevItems.map((i) => (i.investing_id === item.investing_id ? { ...i, marked: null } : i)),
    );
    api.post(`/watchlist-extended/${item.investing_id}/unmark`).catch((err) => {
      setError(err.response?.data?.message || "Error unmarking item.");
      setItems((prevItems) =>
        prevItems.map((i) => (i.investing_id === item.investing_id ? { ...i, marked: item.marked } : i)),
      );
    });
  }

  function openTickerChart(ticker: string, instrumentId: number, companyName: string) {
    if (props?.containerApi) {
      addFloatingPanel(props.containerApi, "realtime_chart", {
        ticker: ticker,
        instrument_id: instrumentId,
        company_name: companyName,
      });
    }
  }

  function openIBKR(item: ExtendedWatchlistItem) {
    const day = new Date().getDay(); // 0=Sun,1=Mon,2=Tue,3=Wed,4=Thu,5=Fri,6=Sat
    const req = {
      ticker: item.ticker,
      right: item.premarket_change_rel >= 0 ? "p" : "c",
      start: String(item.premarket_last),
      directional_limit: "0",
      skip_expiries: day === 4 || day === 5 ? "1" : "0",
      number_of_expiries: "1",
    };
    localStorage.setItem("ibkr_totalViewReq", JSON.stringify(req));
    if (props?.containerApi) {
      addFloatingPanel(props.containerApi, "ibkr", {});
    }
  }

  // Save scroll position and scroll to top when article opens; restore when it closes
  React.useEffect(() => {
    if (!containerRef.current) return;
    if (activeArticleUrl) {
      savedScrollTop.current = containerRef.current.scrollTop;
      containerRef.current.scrollTop = 0;
    } else {
      containerRef.current.scrollTop = savedScrollTop.current;
    }
  }, [activeArticleUrl]);

  const tf = tickerFilter.trim().toLowerCase();

  return (
    <div ref={containerRef} className="p-4 h-full overflow-auto relative">
      {error && <div className="text-red-500 mb-4">{error}</div>}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          fetchWatchlistExtended();
        }}
        className="flex flex-col sm:flex-row gap-6 mb-4 items-start flex-wrap"
      >
        <div className="flex flex-col">
          <span className="font-semibold mb-1">Group By</span>
          {(["all", "by_sector", "marked_only", "in_watchlist"] as GroupBy[]).map((v) => (
            <label key={v} className="flex items-center gap-1">
              <input type="radio" name="ext_group_by" value={v} checked={groupBy === v} onChange={() => setGroupBy(v)} />
              {v}
            </label>
          ))}
        </div>

        <div className="flex flex-col">
          <span className="font-semibold mb-1">Sort By</span>
          <label className="flex items-center gap-1">
            <input
              type="radio"
              name="ext_sort_by"
              value="regular_hours_change_rel"
              checked={sortBy === "regular_hours_change_rel"}
              onChange={() => setSortBy("regular_hours_change_rel")}
            />
            regular_hours_change_rel
          </label>
          <label className="flex items-center gap-1">
            <input
              type="radio"
              name="ext_sort_by"
              value="premarket_change_rel"
              checked={sortBy === "premarket_change_rel"}
              onChange={() => setSortBy("premarket_change_rel")}
            />
            premarket_change_rel
          </label>
          <label className="flex items-center gap-1">
            <input
              type="radio"
              name="ext_sort_by"
              value="abs_pre_minus_reg"
              checked={sortBy === "abs_pre_minus_reg"}
              onChange={() => setSortBy("abs_pre_minus_reg")}
            />
            |pre - reg|
          </label>
        </div>

        <div className="flex flex-col">
          <span className="font-semibold mb-1">Order</span>
          {(["desc", "asc"] as Order[]).map((v) => (
            <label key={v} className="flex items-center gap-1">
              <input type="radio" name="ext_order" value={v} checked={order === v} onChange={() => setOrder(v)} />
              {v}
            </label>
          ))}
        </div>

        <div className="flex flex-col gap-2 w-16">
          <label htmlFor="ext-days" className="font-semibold">Days:</label>
          <input
            id="ext-days"
            type="text"
            inputMode="numeric"
            value={daysInput}
            onChange={(e) => setDaysInput(e.target.value)}
            className="border rounded px-2 py-1 w-20 font-mono"
          />
        </div>

        <div className="flex flex-col">
          <span className="font-semibold mb-1">Mark</span>
          {(["any", "0", "1", "2"] as MarkFilter[]).map((v) => (
            <label key={v} className="flex items-center gap-1">
              <input
                type="radio"
                name="ext_mark_filter"
                value={v}
                checked={markFilter === v}
                onChange={() => setMarkFilter(v)}
              />
              {v}
            </label>
          ))}
        </div>

        <div className="flex flex-col gap-2">
          <span className="font-semibold">&nbsp;</span>
          <label className="flex items-center gap-1 cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={onlyWithIntercepts}
              onChange={(e) => setOnlyWithIntercepts(e.target.checked)}
            />
            Only with intercepts
          </label>
        </div>

        <div className="flex items-center justify-between gap-4">
          <button type="submit" className="btn btn-primary h-11">
            Fetch
          </button>
        </div>
      </form>

      <div className="mt-4 flex gap-4">
        <input
          type="text"
          placeholder="Search ticker or name..."
          value={tickerFilter}
          onChange={(e) => setTickerFilter(e.target.value)}
          className="border rounded px-2 py-1 w-60"
        />
        <input
          type="text"
          placeholder="Search sector..."
          value={sectorFilter}
          onChange={(e) => setSectorFilter(e.target.value)}
          className="border rounded px-2 py-1 w-60"
          title="Not wired up yet - no sector data exists for the extended watchlist"
        />
      </div>

      {items.length > 0 && (
        <div className="border-r-2 border-l-2 block sm:inline-block sm:mr-4 mt-4 md:min-w-[600px]">
          <div className="bg-slate-300 p-1 flex justify-between">
            <span>{sector}</span>
            <span>{total}</span>
          </div>
          <div className="grid grid-cols-[auto_auto_auto_auto] sm:grid-cols-[auto_auto_auto_auto_auto_auto] gap-x-4">
            {items.map((item) => {
              const isItemMatch = tf.length >= 2 && (item.ticker.toLowerCase().startsWith(tf) || item.company_name.toLowerCase().startsWith(tf));
              const highlightClass = isItemMatch ? "bg-yellow-200" : "";

              return (
                <div
                  key={item.investing_id}
                  className={`col-span-4 sm:col-span-6 grid grid-cols-subgrid ${highlightClass}`}
                >
                  <a
                    className="px-1 pt-2 cursor-pointer"
                    title={item.company_name}
                    onClick={() => openTickerChart(item.ticker, item.investing_id, item.company_name)}
                  >
                    {item.ticker}
                  </a>
                  <span className="px-1 pt-2 text-right">
                    {item.regular_hours_last.toFixed(2)}
                  </span>
                  <span className="px-1 pt-2 text-right">
                    {item.regular_hours_change_abs.toFixed(2)}
                  </span>
                  <span className="px-1 pt-2 text-right">
                    {item.regular_hours_change_rel.toFixed(2)}%
                  </span>
                  <button
                    type="button"
                    className={`hidden sm:block px-1 pt-2 text-sm rounded transition-colors ${item.marked ? "bg-gray-700 text-white hover:bg-gray-600" : "hover:bg-gray-200"}`}
                    onClick={() => (item.marked ? handleUnmark(item) : handleMark(item))}
                    title={item.marked ?? undefined}
                  >
                    {item.marked ? "Unmark" : "Mark"}
                  </button>
                  <button
                    type="button"
                    className="hidden sm:block px-1 pt-2 text-sm rounded transition-colors hover:bg-gray-200"
                    onClick={() => openIBKR(item)}
                  >
                    IBKR
                  </button>

                  <span className="px-1 pb-1 text-gray-400">pre:</span>
                  <span className="px-1 pb-1 text-right text-gray-400">
                    {item.premarket_last.toFixed(2)}
                  </span>
                  <span className="px-1 pb-1 text-right text-gray-400">
                    {item.premarket_change_abs.toFixed(2)}
                  </span>
                  <span className="px-1 pb-1 text-right text-gray-400">
                    {item.premarket_change_rel.toFixed(2)}%
                  </span>
                  <a
                    onClick={() => toggleIntercepted(item.investing_id)}
                    className={`hidden sm:block cursor-pointer sm:col-span-2 px-1 pb-1 ${item.intercepted.length === 0 ? "text-gray-400" : "font-bold"}`}
                  >
                    {item.intercepted.length} intercepted {expandedIntercepted.has(item.investing_id) ? "▲" : "▼"}
                  </a>

                  {/* Mobile-only buttons row */}
                  <div className="sm:hidden col-span-4 flex gap-4 px-1 pb-1 items-start">
                    <button
                      type="button"
                      onClick={() => toggleIntercepted(item.investing_id)}
                      className={`text-sm ${item.intercepted.length === 0 ? "text-gray-400" : "font-bold"}`}
                    >
                      {item.intercepted.length} intercepted {expandedIntercepted.has(item.investing_id) ? "▲" : "▼"}
                    </button>
                    <div className="flex flex-col gap-0.5 text-sm">
                      <button
                        type="button"
                        className={`rounded transition-colors ${item.marked ? "bg-gray-700 text-white hover:bg-gray-600" : "hover:bg-gray-200"}`}
                        onClick={() => (item.marked ? handleUnmark(item) : handleMark(item))}
                        title={item.marked ?? undefined}
                      >
                        {item.marked ? "Unmark" : "Mark"}
                      </button>
                      <button type="button" className="rounded transition-colors hover:bg-gray-200" onClick={() => openIBKR(item)}>
                        IBKR
                      </button>
                    </div>
                  </div>

                  {expandedIntercepted.has(item.investing_id) && (
                    <ExtendedWatchlistIntercepted
                      investingId={item.investing_id}
                      companyName={item.company_name}
                      items={item.intercepted}
                      setActiveArticleUrl={setActiveArticleUrl}
                      markItem={markItem}
                    />
                  )}

                  <div className="col-span-4 sm:col-span-6 border-b border-gray-300"></div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {isLoading && <div className="mt-4">Loading...</div>}
      {!isLoading && items.length === 0 && !error && <div className="mt-4 text-gray-400">No items</div>}
      {items.length > 0 && (
        <div className="mt-2 text-sm text-gray-500">
          {items.length} / {total}
        </div>
      )}
      <div ref={sentinelRef} className="h-px" />

      {activeArticleUrl !== "" && <ArticleViewer url={activeArticleUrl} setActiveArticleUrl={setActiveArticleUrl} />}
    </div>
  );
};
